import toast from 'react-hot-toast';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import { queryClient } from '../../shared/providers/react-query-provider';
import { sendErrorReport } from '../../shared/utilities/sendErrorReport';
import { markMessageAsReadInCache } from '../dossier/cache-utils';

import * as actions from './actions';
import * as api from './api';
import {
    getSendingMessageById,
    getDiscussionAttachments as getDiscussionAttachmentsSelector,
} from './selectors';
import {
    SendMessageRequestAction,
    GetMessageRequestAction,
    UploadAttachmentRequestAction,
    SendingMessage,
    Attachment,
    ScheduleMessageRequestAction,
    FetchScheduledMessagesRequestAction,
    DeleteScheduledMessageRequestAction,
    MarkMessageAsReadRequestAction,
    GetDraftMessageRequestAction,
    SaveDraftMessageAction,
    SendMessageAPIResponse,
    Message,
    UploadAttachmentAPI,
    ScheduleMessageAPIResponse,
    ScheduledMessage,
    MessageAttachment,
} from './types';
import { AllDiscussionMessagesResponse, getDiscussionMessagesQueryKey } from './queries';

function* sendMessageFlow({
    dossierId,
    sendingMessageId,
    contentHtml,
}: SendMessageRequestAction): SagaIterator {
    try {
        const sendingMessage: SendingMessage = yield select(
            getSendingMessageById(dossierId, sendingMessageId),
        );
        const attachmentIds = Object.values(sendingMessage.attachments || ({} as Attachment)).map(
            (x) => x.id,
        );
        const apiResponse: SendMessageAPIResponse = yield call(
            api.sendMessage,
            sendingMessage.content,
            dossierId,
            attachmentIds,
            contentHtml,
        );

        yield put(actions.sendMessageSuccess(apiResponse.messageId, dossierId, sendingMessageId));
        yield put(actions.getMessageRequest(apiResponse.messageId, dossierId, sendingMessageId));
        yield put(actions.saveDraftMessage(dossierId, '', ''));
    } catch (e) {
        yield put(actions.sendMessageFailure((e as Error).message, sendingMessageId, dossierId));
        sendErrorReport(e as Error);
    }
}

function* getOneMessageFlow({
    messageId,
    sendingMessageId,
    dossierId,
}: GetMessageRequestAction): SagaIterator {
    try {
        const apiResponse: Message = yield call(api.getOneMessage, messageId);
        yield put(actions.getMessageSuccess(apiResponse, dossierId, sendingMessageId));

        queryClient.setQueryData<AllDiscussionMessagesResponse>(
            getDiscussionMessagesQueryKey(dossierId),
            (current) => {
                if (!current) return current;

                const messageExists = current.messages.some(
                    (message) => message.id === apiResponse.id,
                );

                if (messageExists) {
                    return {
                        ...current,
                        messages: current.messages.map((message) =>
                            message.id === apiResponse.id ? apiResponse : message,
                        ),
                    };
                }

                return {
                    ...current,
                    messages: [...current.messages, apiResponse],
                };
            },
        );
    } catch (e) {
        yield put(actions.getMessageFailure((e as Error).message, dossierId));
        sendErrorReport(e as Error);
    }
}

function* uploadAttachmentFlow({
    file,
    dossierId,
    sendingMessageId,
    category,
}: UploadAttachmentRequestAction): SagaIterator {
    try {
        const apiResponse: UploadAttachmentAPI = yield call(api.uploadAttachment, file, category);
        yield put(
            actions.uploadAttachmentSuccess(apiResponse.id, dossierId, file.name, sendingMessageId),
        );
    } catch (e) {
        yield put(
            actions.uploadAttachmentFailure(
                (e as Error).message,
                dossierId,
                file.name,
                sendingMessageId,
            ),
        );
        sendErrorReport(e as Error);
    }
}

function* scheduleMessageFlow({
    dossierId,
    content,
    contentHtml,
    scheduledDate,
    authorId,
    sendingMessageId,
}: ScheduleMessageRequestAction): SagaIterator {
    try {
        const sendingMessage: SendingMessage | undefined = yield select(
            getSendingMessageById(dossierId, sendingMessageId),
        );
        const attachmentIds = sendingMessage
            ? Object.values(sendingMessage.attachments || ({} as Attachment)).map((x) => x.id)
            : [];
        const apiResponse: ScheduleMessageAPIResponse = yield call(
            api.scheduleMessage,
            dossierId,
            content,
            contentHtml,
            scheduledDate,
            attachmentIds,
        );

        yield put(
            actions.scheduleMessageSuccess(
                apiResponse.id,
                dossierId,
                content,
                contentHtml,
                scheduledDate,
                authorId,
                sendingMessageId,
            ),
        );
        yield put(actions.fetchScheduledMessagesRequest(dossierId));
        yield put(actions.saveDraftMessage(dossierId, '', ''));
    } catch (e) {
        yield put(actions.scheduleMessageFailure((e as Error).message, dossierId));
        sendErrorReport(e as Error);
    }
}

function* getScheduledMessagesFlow({
    dossierId,
}: FetchScheduledMessagesRequestAction): SagaIterator {
    try {
        const apiResponse: ScheduledMessage | null = yield call(
            api.getScheduledMessages,
            dossierId,
        );

        if (apiResponse === null) {
            yield put(actions.deleteScheduledMessageSuccess(dossierId));
            return;
        }

        yield put(
            actions.fetchScheduledMessagesSuccess(
                apiResponse.id,
                dossierId,
                apiResponse.content,
                apiResponse.contentHtml,
                apiResponse.scheduledDate,
                apiResponse.authorId,
                apiResponse.attachments || [],
            ),
        );
    } catch (e) {
        yield put(actions.fetchScheduledMessagesFailure((e as Error).message, dossierId));
        sendErrorReport(e as Error);
    }
}

function* deleteScheduledMessageFlow({
    dossierId,
    scheduledMessageId,
}: DeleteScheduledMessageRequestAction): SagaIterator {
    try {
        yield call(api.deleteScheduledMessage, dossierId, scheduledMessageId);
        yield put(actions.deleteScheduledMessageSuccess(dossierId));
        toast.success('discussion.schedule_messages.delete_success');
    } catch (e) {
        yield put(actions.deleteScheduledMessageFailure((e as Error).message, dossierId));
        sendErrorReport(e as Error);
    }
}

function* postMarkMessageAsReadFlow({
    dossierId,
    messageId,
}: MarkMessageAsReadRequestAction): SagaIterator {
    try {
        yield call(api.markMessageAsRead, messageId);

        markMessageAsReadInCache(dossierId);
        yield put(actions.markMessageAsReadSuccess(dossierId));
    } catch (e) {
        yield put(actions.markMessageAsReadFailure((e as Error).message));
        sendErrorReport(e as Error);
    }
}

function* getDraftMessageFlow({ dossierId }: GetDraftMessageRequestAction): SagaIterator {
    try {
        const apiResponse: { content: string; contentHtml: string } = yield call(
            api.getDraftMessage,
            dossierId,
        );

        yield put(
            actions.getDraftMessageSuccess(dossierId, apiResponse.content, apiResponse.contentHtml),
        );
    } catch (e) {
        yield put(actions.getDraftMessageFailure((e as Error).message, dossierId));
        toast.error('discussion.draft_message.fetching_error');
        sendErrorReport(e as Error);
    }
}

function* saveDraftMessageFlow({
    dossierId,
    draftMessage,
    htmlDraftMessage,
}: SaveDraftMessageAction): SagaIterator {
    try {
        yield call(api.putDraftMessage, dossierId, draftMessage, htmlDraftMessage);

        yield put(actions.saveDraftMessageSuccess(dossierId));
    } catch (e) {
        yield put(actions.saveDraftMessageFailure((e as Error).message, dossierId));
        sendErrorReport(e as Error);
    }
}

function* getDiscussionAttachmentsFlow({ dossierId }: GetDraftMessageRequestAction): SagaIterator {
    try {
        const attachments = yield select(getDiscussionAttachmentsSelector(dossierId));

        if (attachments.length > 0) {
            yield put(actions.getDiscussionAttachmentsSuccess(dossierId, attachments));
            return;
        }

        const apiResponse: MessageAttachment[] = yield call(
            api.getDiscussionAttachments,
            dossierId,
        );

        yield put(actions.getDiscussionAttachmentsSuccess(dossierId, apiResponse));
    } catch (e) {
        yield put(actions.getDiscussionAttachmentsFailure((e as Error).message, dossierId));
        toast.error('global.error.failed_fetch');
        sendErrorReport(e as Error);
    }
}

export function* discussionWatcher(): SagaIterator {
    yield takeEvery(actions.sendMessageRequestAction, sendMessageFlow);
    yield takeEvery(actions.getMessageRequestAction, getOneMessageFlow);
    yield takeEvery(actions.uploadAttachmentRequestAction, uploadAttachmentFlow);
    yield takeLatest(actions.scheduleMessageRequestAction, scheduleMessageFlow);
    yield takeLatest(actions.fetchScheduledMessagesRequestAction, getScheduledMessagesFlow);
    yield takeEvery(actions.deleteScheduledMessageRequestAction, deleteScheduledMessageFlow);
    yield takeEvery(actions.markMessageAsReadRequestAction, postMarkMessageAsReadFlow);
    yield takeLatest(actions.getDraftMessageRequestAction, getDraftMessageFlow);
    yield takeLatest(actions.saveDraftMessageAction, saveDraftMessageFlow);
    yield takeLatest(actions.getDiscussionAttachmentsRequestAction, getDiscussionAttachmentsFlow);
}
